import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { LayoutList, Plus, Search } from 'lucide-react'
import { useI18n } from '@/i18n'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { Input, Select } from '@/components/ui/Field'
import { Modal } from '@/components/ui/Modal'
import { EmptyState, Skeleton } from '@/components/ui/Feedback'
import { useToast } from '@/components/ui/Toast'
import { RequireOwner } from '@/components/auth/RequireOwner'
import { StudioNav } from '@/components/studio/StudioNav'
import { ListingRow } from '@/components/studio/ListingRow'
import { useWatches } from '@/hooks/useWatches'
import type { ListingStatus, Watch } from '@/types'

/**
 * Manage listings — the owner's view of everything in the boutique, drafts
 * included. Status is editable straight from the row: hiding a piece is one
 * click, not a trip through the edit form.
 */
export default function StudioListings() {
  return (
    <RequireOwner>
      <Listings />
    </RequireOwner>
  )
}

function Listings() {
  const { t, tf } = useI18n()
  const { toast } = useToast()
  const { watches, loading, update, remove } = useWatches()

  const [q, setQ] = useState('')
  const [status, setStatus] = useState<'' | ListingStatus>('')
  const [pendingDelete, setPendingDelete] = useState<Watch | null>(null)
  const [deleting, setDeleting] = useState(false)

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return watches
      .filter((w) => (status ? w.status === status : true))
      .filter((w) =>
        needle
          ? [w.title, w.title_vi, w.reference, w.maison].filter(Boolean).join(' ').toLowerCase().includes(needle)
          : true,
      )
      .sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime())
  }, [watches, q, status])

  const changeStatus = async (watch: Watch, next: ListingStatus) => {
    await update(watch.id, { status: next })
    toast(t('studio.statusChanged'))
  }

  const confirmDelete = async () => {
    if (!pendingDelete) return
    setDeleting(true)
    await remove(pendingDelete.id)
    setDeleting(false)
    setPendingDelete(null)
    toast(t('studio.deleted'))
  }

  return (
    <Container size="xl" data-upbo-id="page-studio-listings" data-upbo-label="Manage listings">
      <div className="pt-10 pb-6 flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="eyebrow">{t('studio.eyebrow')}</span>
          <h1 className="font-display text-[36px] sm:text-[44px] leading-tight">{t('studio.manageTitle')}</h1>
          <p className="text-sm text-text-muted">{t('studio.manageSubtitle')}</p>
        </div>
        <Link to="/studio/new">
          <Button icon={<Plus size={15} strokeWidth={1.5} />}>{t('studio.newListing')}</Button>
        </Link>
      </div>

      <StudioNav />

      {/* ── Search + status filter ─────────────────────────────────────── */}
      <div className="flex flex-wrap items-end gap-3 py-6">
        <div className="flex-1 min-w-[220px]">
          <Input
            icon={<Search size={15} strokeWidth={1.5} />}
            placeholder={t('studio.searchListings')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="w-[190px]">
          <Select
            aria-label={t('studio.filterStatus')}
            options={[
              { value: '', label: t('common.all') },
              { value: 'published', label: t('status.published') },
              { value: 'draft', label: t('status.draft') },
              { value: 'reserved', label: t('status.reserved') },
              { value: 'sold', label: t('status.sold') },
            ]}
            value={status}
            onChange={(e) => setStatus(e.target.value as '' | ListingStatus)}
          />
        </div>
      </div>

      {/* ── Column headings (desktop only) ─────────────────────────────── */}
      <div className="hidden lg:grid grid-cols-[64px_minmax(0,1fr)_140px_150px_90px_120px_auto] gap-4 pb-3 border-b border-border">
        {['', 'studio.tableWatch', 'studio.tablePrice', 'studio.tableStatus', 'studio.tableViews', 'studio.tableDate', 'studio.tableActions'].map(
          (key, i) => (
            <span key={i} className="text-[10px] uppercase tracking-[0.16em] text-text-faint">
              {key ? t(key) : ''}
            </span>
          ),
        )}
      </div>

      {/* ── Rows ───────────────────────────────────────────────────────── */}
      <div className="pb-20">
        {loading ? (
          <div className="flex flex-col gap-3 pt-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-20 w-full" />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<LayoutList size={22} strokeWidth={1.5} />}
            title={t('studio.emptyTitle')}
            description={t('studio.emptyBody')}
            action={
              <Link to="/studio/new">
                <Button>{t('studio.newListing')}</Button>
              </Link>
            }
          />
        ) : (
          rows.map((w) => (
            <ListingRow
              key={w.id}
              watch={w}
              onStatus={(next) => void changeStatus(w, next)}
              onDelete={() => setPendingDelete(w)}
            />
          ))
        )}
      </div>

      {/* ── Delete confirmation ────────────────────────────────────────── */}
      <Modal
        open={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title={t('studio.deleteTitle')}
        description={pendingDelete ? t('studio.deleteBody', { title: tf(pendingDelete, 'title') }) : ''}
        footer={
          <div className="flex items-center gap-3 justify-end">
            <Button variant="ghost" onClick={() => setPendingDelete(null)}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" loading={deleting} onClick={() => void confirmDelete()}>
              {t('common.delete')}
            </Button>
          </div>
        }
      >
        {pendingDelete && (
          <div className="flex items-center gap-4">
            <img
              src={pendingDelete.image}
              alt=""
              className="h-16 w-16 object-cover rounded-squircle bg-surface-2"
            />
            <div className="flex flex-col gap-0.5">
              <span className="font-display text-lg">{tf(pendingDelete, 'title')}</span>
              <span className="text-[11px] uppercase tracking-[0.14em] text-text-faint">
                {pendingDelete.reference}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </Container>
  )
}
